<template>
  <CCard class="mt-4">
    <CCardHeader
      style="background-color: #145654"
      class="text-white d-flex justify-content-between align-items-center"
    >
      <h4 class="mb-0">
        <CIcon name="cil-description" /> {{ $t('ticketDetail.title') }}
      </h4>
      <CButton color="light" size="sm" @click="$emit('close')">
        <CIcon name="cil-x" />
      </CButton>
    </CCardHeader>

    <CCardBody class="p-4">
      
      <div class="work-order-timeline my-4">
        <div class="timeline-line" :style="getTimelineStyle()"></div>
        <div
          v-for="step in timelineSteps"
          :key="step.num"
          :class="['timeline-step', { active: getCurrentStep() >= step.num }]"
        >
          <div v-if="step.num === 1 && getCurrentStep() === 1" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/bb3a855a-a6d2-443c-b6a9-523a0a416476/jYe7PDPdCV.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 2 && getCurrentStep() === 2" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/ebf1b394-df01-458b-a3eb-dd0129aceab2/m4Y81rb9Rp.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 3 && getCurrentStep() === 3" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/b1900545-252b-4418-af8e-2f8427524719/v4NfdUTB1W.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 4 && report.status === 'done'" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/6078979a-4896-49cf-9f62-964f5bf12ad4/9eGbn1SSa9.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div class="step-circle"></div>
          <div class="step-label">
            <div class="step-status">{{ step.status }}</div>
            <div class="step-role">{{ step.role }}</div>
          </div>
        </div>
      </div>

      <div
        v-if="['done', 'welldone'].includes(report.status)"
        class="evaluation-box text-center p-4 mb-4"
      >
        <h5 class="font-weight-bold text-dark mb-2">
          {{
            report.status === "done"
              ? $t('ticketDetail.evalPlease')
              : $t('ticketDetail.evalResult')
          }}
        </h5>

        <div class="star-rating mb-3">
          <span
            v-for="star in 5"
            :key="star"
            class="star"
            :class="{
              filled: star <= (hoverRating || rating),
              disabled: report.status === 'welldone',
            }"
            @mouseover="report.status === 'done' ? (hoverRating = star) : null"
            @mouseleave="report.status === 'done' ? (hoverRating = 0) : null"
            @click="report.status === 'done' ? (rating = star) : null"
            >★</span
          >
        </div>

        <div v-if="rating > 0" class="mb-3 text-warning font-weight-bold">
          {{ getRatingText(rating) }}
        </div>

        <CButton
          v-if="report.status === 'done'"
          color="success"
          size="lg"
          @click="submitEvaluation"
          :disabled="isSubmitting"
          class="px-5 shadow-sm"
        >
          <span v-if="!isSubmitting">{{ $t('ticketDetail.evalSubmit') }}</span>
          <span v-else>{{ $t('ticketDetail.saving') }}</span>
        </CButton>
      </div>

      <div v-if="report.status === 'cancelled'" class="alert alert-danger p-4 mb-4 shadow-sm" style="border-radius: 10px;">
        <h5 class="font-weight-bold mb-2"><CIcon name="cil-ban" size="lg" class="mb-1 mr-2"/> {{ $t('ticketDetail.cancelWhy') }}</h5>
        <div class="text-dark bg-white p-3 rounded" style="border: 1px solid #f5c6cb;">
          {{ report.cancelReason || $t('ticketDetail.noReason') }}
        </div>
      </div>

      <hr class="border-dashed my-4" />

      <div class="mx-3">
        <div v-if="report.status !== 'pending'" class="mb-4 p-3 bg-white border rounded shadow-sm">
           <h6 class="font-weight-bold mb-3 text-primary border-bottom pb-2">{{ $t('ticketDetail.assignees') }}</h6>
           <CRow>
             <CCol sm="6">
                <label class="lbl">{{ $t('ticketDetail.supervisorAssigner') }}</label>
                <div>{{ getSupervisorName() }}</div>
                <small class="text-muted" v-if="report.supervisorPart && report.supervisorPart.dateTimeInformed">
                   {{ $t('ticketDetail.when') }} {{ formatDate(report.supervisorPart.dateTimeInformed) }}
                </small>
             </CCol>
             <CCol sm="6">
                <label class="lbl">{{ $t('ticketDetail.technician') }}</label>
                <div class="text-primary font-weight-bold" style="font-size: 1.1em;">
                   {{ getTechnicianName() }}
                </div>
                <div class="font-weight-bold mt-1" style="color: #fd7e14; font-size: 1rem;" v-if="report.status === 'assigned'">
                   {{ $t('ticketDetail.waitTechAccept') }}
                </div>
                <small class="text-muted" v-if="getCompletionDate()">
                   {{ $t('ticketDetail.finishedWhen') }} {{ formatDate(getCompletionDate()) }}
                </small>
             </CCol>
           </CRow>
        </div>

        <h5 class="font-weight-bold mb-4">{{ $t('ticketDetail.sectionReport') }}</h5>

        <CRow class="mb-2">
          <CCol sm="12">
            <div class="p-3 bg-light border rounded d-flex align-items-center">
              <div class="mr-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width:40px; height:40px;">
                <CIcon name="cil-tag" />
              </div>
              <div>
                <label class="lbl mb-0 text-muted">{{ $t('ticketDetail.ticketNo') }}</label>
                <div class="text-primary font-weight-bold h4 mb-0">
                  {{ report.ticketNumber || '-' }}
                </div>
              </div>
            </div>
          </CCol>
        </CRow>

        <div class="mb-4 mt-2">
          <CRow class="mb-3">
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.reporter') }}</label>
              <div class="text-dark">{{ getUserName() }} <span v-if="getOnBehalfOf()" class="text-danger font-weight-bold ml-1" style="font-size: 0.9em;">({{ $t('ticketDetail.onBehalf') }} {{ getOnBehalfOf() }})</span></div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.deptCombined') }}</label>
              <div class="text-dark">{{ getDepartment() }}</div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.phonePersonalShort') }}</label>
              <div class="text-dark">{{ getPersonalPhone() }}</div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.phoneWorkShort') }}</label>
              <div class="text-dark">{{ getOfficePhone() }}</div>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.jobType') }}</label>
              <div class="text-dark">{{ getCategoryName() }}</div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.workArea') }}</label>
              <div class="text-dark">{{ getLocationFull() }}</div>
            </CCol>
            <CCol sm="12" md="6" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.timeSlot') }}</label>
              <div class="text-dark">{{ getAvailableTime() }}</div>
            </CCol>
          </CRow>
        </div>

        <CRow v-if="isOutdoor()" class="mb-4">
          <CCol>
            <label class="lbl">{{ $t('ticketDetail.mapPoint') }}</label>
            <div class="map-container border rounded shadow-sm overflow-hidden" style="height: 350px;">
              <div ref="mapContainer" style="width: 100%; height: 100%"></div>
            </div>
            <div class="mt-2 text-right">
              <a 
                :href="`https://www.google.com/maps?q=${mapPosition.lat},${mapPosition.lng}`" 
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <CIcon name="cil-map" /> {{ $t('ticketDetail.openMaps') }}
              </a>
            </div>
          </CCol>
        </CRow>

        <CRow v-if="getMachineNumberImage()" class="mb-4">
          <CCol>
            <label class="lbl">{{ $store.state.locale.lang === 'en' ? 'Machine number / asset number photo' : 'รูปหมายเลขเครื่อง / เลขครุภัณฑ์' }}</label>
            <div class="text-center bg-light p-2 rounded border" style="cursor: pointer" @click="openImage(getMachineNumberImage())">
              <img :src="getImageUrl(getMachineNumberImage())" class="img-fluid rounded" style="max-height: 250px" @error="handleImageError" />
            </div>
          </CCol>
        </CRow>

        <CRow class="mb-3">
          <CCol><label class="lbl">{{ $t('ticketDetail.problem') }}</label><div class="p-3 bg-light rounded border" v-html="getProblemDetails()"></div></CCol>
        </CRow>

        <CRow v-if="getProblemImage()" class="mb-4">
          <CCol>
            <label class="lbl">{{ $t('ticketDetail.photoBefore') }}</label>
            <div class="text-center bg-light p-2 rounded border" style="cursor: pointer" @click="openImage(getProblemImage())">
              <img :src="getImageUrl(getProblemImage())" class="img-fluid rounded" style="max-height: 250px" @error="handleImageError" />
            </div>
          </CCol>
        </CRow>

        <div v-if="['done', 'welldone'].includes(report.status)">
          <h5 class="font-weight-bold mb-3 mt-5 text-success">{{ $t('ticketDetail.workOutcome') }}</h5>
          
          <CRow class="mb-3">
            <CCol>
              <label class="lbl">{{ $t('ticketDetail.fixDetail') }}</label>
              <div class="p-3 bg-success-light rounded border border-success">
                {{ getMaintenanceDetails() || '-' }}
              </div>
            </CCol>
          </CRow>

          <CRow v-if="getMaintenanceImage()" class="mb-4">
            <CCol>
              <label class="lbl">{{ $t('ticketDetail.photoAfter') }}</label>
              <div class="text-center bg-light p-2 rounded border" style="cursor: pointer" @click="openImage(getMaintenanceImage())">
                <img :src="getImageUrl(getMaintenanceImage())" class="img-fluid rounded" style="max-height: 250px" @error="handleImageError" />
              </div>
            </CCol>
          </CRow>
        </div>

      </div>

      <CModal :show.sync="imageModal" centered size="lg">
        <img :src="selectedImageUrl" class="img-fluid w-100" />
        <template #footer><CButton color="secondary" @click="imageModal = false">{{ $t('ticketDetail.close') }}</CButton></template>
      </CModal>
    </CCardBody>
  </CCard>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import reportDetailMixin from "@/i18n/reportDetailMixin";
import { formatAvailableTimeDisplay } from "@/i18n/formatAvailableTime";

export default {
  name: "UReportDetail",
  mixins: [reportDetailMixin],
  props: { report: { type: Object, required: true } },
  data: () => ({
    imageModal: false,
    selectedImageUrl: null,
    rating: 5,
    hoverRating: 0,
    isSubmitting: false,
    map: null,
    marker: null,
  }),
  created() {
    if (this.report.status === "welldone" && this.report.rating) {
      this.rating = this.report.rating.stars;
    }
  },
  mounted() {
    if (this.isOutdoor()) {
      this.$nextTick(() => {
        this.initMap();
      });
    }
  },
  beforeDestroy() {
    if (this.map) {
      this.map = null;
      this.marker = null;
    }
  },
  computed: {
    mapPosition() {
      const loc = this.report?.userPart?.outdoorLocation;
      if (loc && loc.latitude && loc.longitude) {
        return {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude)
        };
      }
      return { lat: 0, lng: 0 };
    }
  },
  watch: {
    report: {
      deep: true,
      handler(val) {
        if (this.isOutdoor()) {
          this.$nextTick(() => {
            this.initMap();
          });
        }
      }
    }
  },
  methods: {
    async submitEvaluation() {
      this.isSubmitting = true;
      try {
        await axios.post(
          `${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/rating`,
          { stars: this.rating }
        );
        await Swal.fire({
          icon: "success",
          title: this.$t('ticketDetail.swalEvalOk'),
          confirmButtonColor: "#2ba9a5",
          timer: 1600,
          showConfirmButton: false,
        });
        this.$emit("refresh");
        this.$emit("close");
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: this.$t('ticketDetail.swalError'), text: this.$t('ticketDetail.swalSaveFail') });
      } finally {
        this.isSubmitting = false;
      }
    },
    getCurrentStep() {
      const map = { pending: 1, assigned: 2, in_progress: 3, done: 4, welldone: 4 };
      return map[this.report.status] || 1;
    },
    getTimelineStyle() {
      const p = { 1: 0, 2: 33, 3: 66, 4: 100 }[this.getCurrentStep()] || 0; 
      return { background: `linear-gradient(to right, #007bff ${p}%, #e0e0e0 ${p}%)` };
    },
    getImageUrl(f) {
      if (!f) return "";
      const filename = f.split("/").pop();
      return `${process.env.VUE_APP_APP_URL}/uploads/${filename}`;
    },
    openImage(img) {
      this.selectedImageUrl = this.getImageUrl(img);
      this.imageModal = true;
    },
    handleImageError(e) { console.error("Image failed to load"); },

    getUserName() {
      return this.report.userPart?.user ? `${this.report.userPart.user.name} ${this.report.userPart.user.surname}` : "-";
    },

    getOnBehalfOf() {
      if (this.report && this.report.onBehalfOf) return this.report.onBehalfOf;
      return this.report && this.report.userPart && this.report.userPart.onBehalfOf;
    },
    getPersonalPhone() { return this.report.userPart?.personalPhone || "-"; },
    getOfficePhone() { return this.report.userPart?.telephoneWork || this.report.userPart?.user?.telephoneWork || "-"; },
    getAvailableTime() {
      const t = this.report.userPart?.availableTime;
      if (!t) return "-";
      void this.$store.state.locale.lang;
      const lang = this.$store.state.locale.lang === "en" ? "en" : "th";
      return formatAvailableTimeDisplay(t, lang) + this.$t("ticketDetail.timeSuffix");
    },
    getDepartment() { return this.report.userPart?.category?.departmentName || "-"; },
    getCategoryName() { return this.report.userPart?.subCategory || "-"; },
    
    getLocationFull() {
      const u = this.report.userPart;
      if (!u) return "-";
      if (u.locationType !== "building") return this.$t('ticketDetail.outdoor');
      let bName = null;
      if (u.buildingLocation?.building) {
         if (typeof u.buildingLocation.building === 'object') {
             bName = u.buildingLocation.building.BUILDINGCODE || u.buildingLocation.building.BUILDINGName || null;
         } else {
             bName = u.buildingLocation.building;
         }
      }
      const r = u.buildingLocation?.roomName || '-';
      if (bName) {
        return this.$t('ticketDetail.locationBuilding').replace('{b}', bName).replace('{r}', r);
      }
      const dashIdx = r.indexOf('-');
      if (dashIdx > 0) {
        const parsedBuilding = r.substring(0, dashIdx).trim();
        const parsedRoom = r.substring(dashIdx + 1).trim();
        return this.$t('ticketDetail.locationBuilding').replace('{b}', parsedBuilding).replace('{r}', parsedRoom);
      }
      return r;
    },
    getProblemDetails() { return this.report.userPart?.problemDetails || "-"; },
    getMachineNumberImage() { return this.report.userPart?.machineNumberImage; },
    getProblemImage() { return this.report.userPart?.problemImage; },
    
    getMaintenanceImage() { return this.report.technicianPart?.maintenanceImage; },
    getMaintenanceDetails() { return this.report.technicianPart?.maintenanceDetails; },
    getCompletionDate() { return this.report.technicianPart?.dateTimeFinished; },

    getSupervisorName() {
       const sup = this.report.supervisorPart?.supervisor;
       if (!sup) return "-";
       if (typeof sup === 'object') {
          return `${sup.name || ''} ${sup.surname || ''}`.trim();
       }
       return "-";
    },
    getTechnicianName() {
       const tech = this.report.supervisorPart?.assignedTechnician;
       let name = this.$t('ticketDetail.techUnknown');
       if (!tech) name = "-";
       else if (typeof tech === 'object') {
           name = `${tech.name || ''} ${tech.surname || ''}`.trim();
       }
       if (this.report.status === 'assigned' && name === "-") {
           return this.$t('ticketDetail.techPending');
       }
       return name;
    },
    formatDate(dateString) {
        if(!dateString) return "-";
        const d = new Date(dateString);
        const loc = this.$store.state.locale.lang === 'en' ? 'en-GB' : 'th-TH';
        return d.toLocaleString(loc);
    },
    isOutdoor() {
      return this.report?.userPart?.locationType === 'outdoor' &&
        this.report?.userPart?.outdoorLocation?.latitude &&
        this.report?.userPart?.outdoorLocation?.longitude;
    },
    initMap() {
      if (!this.isOutdoor() || !this.$refs.mapContainer) return;
      
      // ตรวจสอบว่า Google Maps API โหลดแล้วหรือยัง
      if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
        console.error('Google Maps API is not loaded. Please check script tag in index.html');
        setTimeout(() => this.initMap(), 100);
        return;
      }

      const position = { lat: this.mapPosition.lat, lng: this.mapPosition.lng };
      
      // สร้างแผนที่ Google Maps
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: position,
        zoom: 16,
        mapTypeId: google.maps.MapTypeId.HYBRID
      });

      // เพิ่ม marker
      this.marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: this.$t('ticketDetail.mapPinTitle')
      });
    }
  },
};
</script>

<style scoped>
/* CSS styles (คงเดิม) */
.action-box {
  background-color: #fff;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.bg-success-light {
  background-color: #d4edda;
  color: #155724;
}

.lbl {
  font-weight: 700;
  color: #495057;
  display: block;
  font-size: 0.85rem;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.work-order-timeline {
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
}

.timeline-line {
  position: absolute;
  top: 15px;
  left: 12%;
  right: 12%;
  height: 3px;
  z-index: 0;
  background: #e0e0e0;
}

.timeline-step {
  position: relative;
  z-index: 1;
  text-align: center;
  width: 25%;
}

.step-circle {
  width: 30px;
  height: 30px;
  background: #fff;
  border: 4px solid #e0e0e0;
  border-radius: 50%;
  margin: 0 auto 5px;
}

.active .step-circle {
  border-color: #007bff;
  background: #007bff;
}

.step-status {
  font-weight: bold;
  font-size: 0.85rem;
  color: #000000;
}

.step-role {
  font-size: 0.75rem;
  color: #888;
}

/* ส่วนประเมิน */
.evaluation-box { background-color: #fff8e1; border: 2px dashed #ffc107; border-radius: 10px; }
.star-rating { font-size: 2.5rem; line-height: 1; cursor: pointer; }
.star { color: #e4e5e9; transition: color 0.2s; margin: 0 5px; }
.star.filled { color: #ffc107; }
.star.disabled { cursor: default; }
.star:not(.disabled):hover { transform: scale(1.1); }

:deep(.gm-style) {
  height: 100%;
  width: 100%;
  z-index: 0;
}
</style>
